import type { SaveSink } from "./export-bundle";

interface SaveFileHandle {
  createWritable(): Promise<WritableStream<Uint8Array>>;
}

interface SavePickerWindow {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<SaveFileHandle>;
}

const BUNDLE_TYPES = [
  { description: "Goalhorns bundle", accept: { "application/zip": [".goalhorns"] } },
];

/**
 * Opens the save target. Must run inside the click handler, before any await, because the
 * file picker needs the user gesture. Resolves to null when the user cancels the picker.
 */
export function openSaveSink(fileName: string): Promise<SaveSink | null> {
  const picker = (window as SavePickerWindow).showSaveFilePicker;
  if (typeof picker !== "function") {
    return Promise.resolve(blobSink(fileName));
  }
  return picker({ suggestedName: fileName, types: BUNDLE_TYPES }).then(
    (handle) => ({
      write: async (stream: ReadableStream<Uint8Array>) => {
        const writable = await handle.createWritable();
        await stream.pipeTo(writable);
      },
    }),
    (error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") return null;
      throw error;
    },
  );
}

/** Fallback without the File System Access API: collect into a Blob and download it. */
export function blobSink(fileName: string): SaveSink {
  return {
    write: async (stream) => {
      const blob = await new Response(stream).blob();
      downloadBlob(blob, fileName);
    },
  };
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
